/* The top of a lead's record: who to ring, why they came, and what is owed today.
 *
 * FIVE FACTS AND NOTHING ELSE: name, phone, email, why they came, what they want.
 * Source category, intent value, created and last-activity dates and the coach
 * assignment are all on the Details tab. None of them is what you read before picking
 * up the phone, and the shipped demo put all of them in the header.
 *
 * THE TRACK SITS BESIDE THE NAME because "what do I owe this person today" is the same
 * question the name is asking, not a separate panel further down.
 *
 * A LOST LEAD RUNS A DIFFERENT CLOCK, from a different day zero: the win-back starts
 * when they said no, not when they enquired. Running the acquisition cadence over
 * somebody who already declined reports every checkpoint overdue and tells you to do
 * things that no longer apply.
 */
import type { ReactNode } from 'react';
import type { Lead } from '@acrostic/model';
import { LEAD_TRACK, LOST_LEAD_TRACK, buildTrack } from '@acrostic/model';
import { Avatar } from '../../components/Avatar.js';
import { TrackCard } from '../../components/Track.js';
import { STAGES, routedTo } from '../../lib/leads.js';

export function LeadHeader({ l, actions }: { l: Lead; actions: ReactNode }) {
  const stage = STAGES[l.stage];
  const lost = l.stage === 'lost';
  const won = l.stage === 'won';

  /* Evidence: everything that could show somebody actually did something. A stage
     move, a note, the last recorded activity. */
  const evidence = [
    l.createdAt,
    ...l.stageHistory.map((h) => h.at),
    ...l.notes.map((n) => n.at),
    ...(l.lastActivity ? [l.lastActivity] : []),
  ];
  const lostAt = [...l.stageHistory].reverse().find((h) => h.stage === 'lost')?.at;
  const dayZero = lost ? (lostAt ?? l.lastActivity ?? l.createdAt) : l.createdAt;
  const track = buildTrack(lost ? LOST_LEAD_TRACK : LEAD_TRACK, dayZero, evidence);

  return (
    <div className="lead-head">
      <section className="panel record-head">
        <Avatar name={l.name} photoUrl={l.photoUrl} size="lg" />
        <div className="idblock">
          <div className="titlerow">
            <h2 className="t-heading-h1">{l.name}</h2>
            <span className={'badge ' + stage.tone + ' t-label-small'}>{stage.long}</span>
          </div>
          {/* Only the contact details the record has. Two stacked dashes under a name
              read as a broken record; one quiet line says what is actually missing. */}
          {(l.phone || l.email)
            ? <div className="t-body-small muted">{[l.phone, l.email].filter(Boolean).join(' · ')}</div>
            : <div className="t-body-small muted">No contact details yet</div>}
          {l.why && <p className="t-body-medium" style={{ marginTop: 8 }}><strong>Why: </strong>{l.why}</p>}
          {l.goal && <p className="t-body-small muted">Goal: {l.goal}</p>}
          <div className="actions">{actions}</div>
        </div>
      </section>

      {/* A won lead has no cadence left to run: they are a member, and the member
          track took over from day zero of the join. */}
      {!won && (
        <TrackCard
          t={track}
          eyebrow={lost ? 'Win-back' : 'Lead track'}
          complete={lost
            ? 'The win-back cadence is finished. Nothing further is scheduled.'
            : 'Every checkpoint met. If they have not joined by now, close it cleanly rather than letting it drift.'}
        >
          {!l.anchor && (
            <p className="t-caption muted" style={{ marginTop: 12 }}>
              {routedTo(l)
                ? `Routed to ${routedTo(l)} · nobody has spoken to them yet.`
                : 'Nobody owns this lead yet.'}
            </p>
          )}
        </TrackCard>
      )}
    </div>
  );
}
